import './styles.css';
import { stages, stageById } from './content/stages';
import { confirmedDate, daysBetween, recentDates, stageForDate } from './core/daily';
import { isHit } from './core/hitTest';
import { loadSave, persist, recordClear } from './core/storage';
import { LocalTelemetry } from './core/telemetry';
import type { Mode, Stage } from './types';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('app_mount_missing');

const loaded = loadSave();
const save = loaded.save;
const log = new LocalTelemetry(save);
const today = (): string => confirmedDate(save);
const button = (label: string, id: string, kind = 'primary'): string => `<button id="${id}" class="button ${kind}">${label}</button>`;
const shell = (body: string, nav = ''): void => { app.innerHTML = `<main class="shell">${body}</main>${nav}`; };
const once = (id: string, listener: () => void): void => document.querySelector(`#${id}`)?.addEventListener('click', listener, { once: true });

if (!save.events.some((event) => event.name === 'first_open')) log.event('first_open');
save.sessionIndex += 1;
log.event('session_start', { session_index: save.sessionIndex, days_since_install: Math.max(0, daysBetween(save.installDate, today())) });
if (loaded.recovered) log.event('app_error', { error_code: 'storage_recovered', screen: 'startup' });

interface Play {
  stage: Stage;
  mode: Mode;
  date: string;
  attempt: number;
  found: Set<string>;
  wrong: number;
  freeHint: boolean;
  rewardHint: boolean;
  continued: boolean;
  remaining: number;
  deadline: number;
  elapsedMs: number;
  activeSince: number;
  timer?: number;
  phase: 'loading' | 'countdown' | 'running' | 'paused' | 'failed' | 'done';
  inputLock: boolean;
}
let play: Play | undefined;
let backgroundStartedAt: number | undefined;

function nav(): string {
  return `<nav class="nav"><button data-nav="home">⌂<span>ホーム</span></button><button data-nav="album">▦<span>アルバム</span></button><button data-nav="settings">⚙<span>設定</span></button><button data-nav="qa">✓<span>QA</span></button></nav>`;
}

function bindNav(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-nav]').forEach((element) => element.addEventListener('click', () => {
    const target = element.dataset.nav;
    if (target === 'home') showHome();
    if (target === 'album') showAlbum('nav');
    if (target === 'settings') showSettings();
    if (target === 'qa') showQa();
  }));
}

function showTutorial(): void {
  const tutorialStartedAt = performance.now();
  if (!save.events.some((event) => event.name === 'tutorial_start')) log.event('tutorial_start', { entry: 'first_open' });
  shell(`<section class="card tutorial"><div class="eyebrow">はじめての練習</div><h1>1つ見つけてみよう</h1><p>左右どちらでも、売店の上の飾りの数が違うところをタップ</p><div class="pair tutorial-pair"><button class="scene tutorial-scene" data-side="left"><img src="./stages/tutorial/base.svg" alt="練習用の左の絵"></button><button class="scene tutorial-scene" data-side="right"><img src="./stages/tutorial/variant.svg" alt="練習用の右の絵"></button></div><p class="rule">2枚の絵から、3つのちがいを60秒以内に見つけよう</p></section>`);
  document.querySelectorAll<HTMLButtonElement>('.tutorial-scene').forEach((scene) => scene.addEventListener('click', (event) => {
    const rect = scene.getBoundingClientRect();
    const pointer = event as PointerEvent;
    if (!isHit({ id: 't1', type: 'count', x: .20, y: .29, shape: 'circle', radius: .072 }, (pointer.clientX - rect.left) / rect.width, (pointer.clientY - rect.top) / rect.height, rect.width, rect.height)) {
      wrongMark(pointer.clientX, pointer.clientY);
      return;
    }
    save.tutorialDone = true;
    persist(save);
    log.event('tutorial_complete', { duration_ms: Math.round(performance.now() - tutorialStartedAt), skipped: false });
    showHome();
  }));
}

function showHome(): void {
  const date = today();
  if (date > save.lastConfirmedDate) {
    save.lastConfirmedDate = date;
    persist(save);
  }
  const stage = stageForDate(date, save);
  const clear = save.clears[date];
  const state = clear ? (clear.clearType === 'practice' ? '練習クリア' : 'クリア') : (save.attempts[date] ? '挑戦中' : '未挑戦');
  log.event('home_view', { content_date: date, daily_state: clear ? 'cleared' : 'new' });
  shell(`<header class="brand"><span class="logo">🔍</span><div><b>60秒まちがい探し</b><small>DAILY / JST</small></div><span class="streak">🔥 連続${save.streakDays}日</span></header>
    <section class="hero"><div class="date">${date}　今日の1問</div><img src=".${stage.base}" alt="${stage.name}"><div class="hero-copy"><span class="status">${state}</span><h1>${stage.name}</h1><p>3つのちがいを60秒以内に見つけよう</p></div>${button(clear ? 'もう一度あそぶ' : '今日の問題をはじめる', 'daily-start')}</section>
    <section class="mini"><b>今日のルール</b><span>⏱ 60秒</span><span>◎ 3か所</span><span>💡 無料ヒント1回</span></section>`, nav());
  once('daily-start', () => startGame(stage, clear ? 'replay' : 'daily', date));
  bindNav();
}

function preload(stage: Stage): Promise<void> {
  return Promise.all([stage.base, stage.variant].map((source) => new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(); image.onerror = () => reject(new Error(`image_load:${source}`)); image.src = `.${source}`;
  }))).then(() => undefined);
}

function startGame(stage: Stage, mode: Mode, date: string): void {
  if (play?.timer) window.clearInterval(play.timer);
  const attempt = (save.attempts[date] ?? 0) + 1;
  if (mode === 'daily' || mode === 'practice') save.attempts[date] = attempt;
  persist(save);
  play = { stage, mode, date, attempt, found: new Set(), wrong: 0, freeHint: false, rewardHint: false, continued: false, remaining: mode === 'replay' || mode === 'practice' ? Number.POSITIVE_INFINITY : 60_000, deadline: 0, elapsedMs: 0, activeSince: 0, phase: 'loading', inputLock: false };
  renderGame();
  preload(stage).then(() => countdown(beginTimer)).catch(() => {
    log.event('app_error', { error_code: 'image_load_failed', screen: 'game', stage_id: stage.id });
    showDialog('画像を読み込めませんでした', '<p>オフライン素材を確認して、もう一度お試しください。</p>', button('ホームへ', 'dialog-home'));
    once('dialog-home', showHome);
  });
}

function renderGame(): void {
  if (!play) return;
  shell(`<header class="game-head"><button id="game-home" aria-label="ホームへ">‹</button><div><b>${play.stage.name}</b><small>${play.mode === 'practice' ? '時間無制限の練習' : play.mode === 'replay' ? 'アルバム再挑戦' : '今日の問題'}</small></div><div class="timer" id="timer">${Number.isFinite(play.remaining) ? '60' : '∞'}</div></header>
    <div class="game-progress"><div><i id="time-bar"></i></div><b><span id="found-count">0</span> / 3</b></div>
    <p class="instruction">左右の絵を見比べて、ちがう場所をタップ</p>
    <section class="pair game-pair"><button class="scene game-scene" data-side="left"><span>左の絵</span><img src=".${play.stage.base}" alt="比較用の左の絵"><i class="marks"></i></button><button class="scene game-scene" data-side="right"><span>右の絵</span><img src=".${play.stage.variant}" alt="違いがある右の絵"><i class="marks"></i></button></section>
    <div class="game-actions">${button('💡 無料ヒント', 'hint', 'hint')}</div><div id="overlay" class="overlay hidden"></div>`);
  document.querySelectorAll<HTMLButtonElement>('.game-scene').forEach((scene) => scene.addEventListener('pointerup', (event) => tapScene(scene, event)));
  document.querySelector('#hint')?.addEventListener('click', useHint);
  document.querySelector('#game-home')?.addEventListener('click', () => abandonGame('home'));
}

function countdown(done: () => void): void {
  if (!play) return;
  play.phase = 'countdown';
  const overlay = document.querySelector<HTMLDivElement>('#overlay');
  if (!overlay) return;
  overlay.classList.remove('hidden');
  let count = 3;
  overlay.innerHTML = `<b class="count">${count}</b>`;
  const timer = window.setInterval(() => {
    if (document.hidden) return;
    count -= 1;
    if (count > 0) overlay.innerHTML = `<b class="count">${count}</b>`;
    else { window.clearInterval(timer); overlay.classList.add('hidden'); done(); }
  }, 1000);
}

function beginTimer(): void {
  if (!play) return;
  play.phase = 'running'; play.activeSince = performance.now();
  if (Number.isFinite(play.remaining)) play.deadline = performance.now() + play.remaining;
  log.event('stage_start', { content_date: play.date, stage_id: play.stage.id, mode: play.mode, attempt_no: play.attempt, difficulty: play.stage.difficulty });
  if (Number.isFinite(play.remaining)) play.timer = window.setInterval(tick, 100);
  updateHud();
}

function activeElapsed(current: Play): number {
  return current.elapsedMs + (current.phase === 'running' && current.activeSince > 0 ? Math.max(0, performance.now() - current.activeSince) : 0);
}

function pauseActive(current: Play): void {
  current.elapsedMs = activeElapsed(current);
  current.activeSince = 0;
}

function tick(): void {
  if (!play || play.phase !== 'running') return;
  play.remaining = Math.max(0, play.deadline - performance.now());
  updateHud();
  if (play.remaining <= 0) failByTimeout();
}

function updateHud(): void {
  if (!play) return;
  const timer = document.querySelector('#timer');
  if (timer) timer.textContent = Number.isFinite(play.remaining) ? String(Math.ceil(play.remaining / 1000)) : '∞';
  const bar = document.querySelector<HTMLElement>('#time-bar');
  if (bar) bar.style.width = `${Number.isFinite(play.remaining) ? Math.min(100, play.remaining / 600) : 100}%`;
  const found = document.querySelector('#found-count');
  if (found) found.textContent = String(play.found.size);
  document.querySelectorAll<HTMLElement>('.marks').forEach((holder) => {
    holder.innerHTML = play ? play.stage.differences.filter((difference) => play?.found.has(difference.id)).map((difference) => `<i class="correct" style="left:${difference.x * 100}%;top:${difference.y * 100}%"></i>`).join('') : '';
  });
  const hint = document.querySelector<HTMLButtonElement>('#hint');
  if (hint) { hint.textContent = play.freeHint ? (play.rewardHint ? 'ヒント使用済み' : '▶ 動画でヒント（仮）') : '💡 無料ヒント'; hint.disabled = play.rewardHint || play.found.size === 3; }
}

function tapScene(scene: HTMLElement, event: PointerEvent): void {
  if (!play || play.phase !== 'running' || play.inputLock) return;
  if (Number.isFinite(play.remaining) && performance.now() >= play.deadline) { play.remaining = 0; failByTimeout(); return; }
  const rect = scene.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width;
  const y = (event.clientY - rect.top) / rect.height;
  const difference = play.stage.differences.find((item) => !play?.found.has(item.id) && isHit(item, x, y, rect.width, rect.height));
  if (!difference) { play.wrong += 1; wrongMark(event.clientX, event.clientY); return; }
  play.inputLock = true; play.found.add(difference.id); playTone(660, 110);
  log.event('difference_found', { content_date: play.date, stage_id: play.stage.id, difference_id: difference.id, elapsed_ms: Math.round(activeElapsed(play)), remaining_sec: Number.isFinite(play.remaining) ? Math.ceil(play.remaining / 1000) : -1, found_count: play.found.size, side: scene.dataset.side });
  navigator.vibrate?.(save.settings.vibration ? 35 : 0);
  updateHud();
  window.setTimeout(() => { if (play) play.inputLock = false; }, 300);
  if (play.found.size === 3) completeGame();
}

function wrongMark(x: number, y: number): void {
  playTone(190, 90);
  const mark = document.createElement('i'); mark.className = 'wrong'; mark.textContent = '×'; mark.style.left = `${x}px`; mark.style.top = `${y}px`; document.body.append(mark); window.setTimeout(() => mark.remove(), 500);
}

function playTone(frequency: number, durationMs: number): void {
  if (!save.settings.sound) return;
  try {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(.035, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(.0001, context.currentTime + durationMs / 1000);
    oscillator.connect(gain); gain.connect(context.destination);
    oscillator.start(); oscillator.stop(context.currentTime + durationMs / 1000);
    oscillator.addEventListener('ended', () => { void context.close(); }, { once: true });
  } catch {
    // Audio is optional; visual feedback always remains available.
  }
}

function useHint(): void {
  if (!play || play.phase !== 'running') return;
  log.event('hint_offer_view', { content_date: play.date, placement: 'game', free_hints_left: play.freeHint ? 0 : 1, found_count: play.found.size });
  if (!play.freeHint) { play.freeHint = true; grantHint('free'); }
  else if (!play.rewardHint) openAd('hint', () => { if (play) play.rewardHint = true; grantHint('rewarded_mock'); });
}

function grantHint(type: 'free' | 'rewarded_mock'): void {
  if (!play) return;
  const difference = play.stage.differences.find((item) => !play?.found.has(item.id));
  if (!difference) return;
  log.event('hint_used', { content_date: play.date, hint_type: type, difference_id: difference.id, remaining_sec: Number.isFinite(play.remaining) ? Math.ceil(play.remaining / 1000) : -1 });
  const target = document.querySelector<HTMLElement>('.game-scene[data-side="right"]');
  const glow = document.createElement('i'); glow.className = 'hint-glow'; glow.style.left = `${difference.x * 100}%`; glow.style.top = `${difference.y * 100}%`; target?.append(glow); window.setTimeout(() => glow.remove(), 2000); updateHud();
}

function failByTimeout(): void {
  if (!play || play.phase !== 'running') return;
  if (play.timer) window.clearInterval(play.timer); pauseActive(play); play.phase = 'failed';
  showFailureDialog();
}

function showFailureDialog(): void {
  if (!play || play.phase !== 'failed') return;
  const canPractice = (save.failures[play.date] ?? 0) >= 1;
  showDialog('時間切れ', `<p>${play.found.size}/3か所。答えは自動で開きません。</p>`, `${button('もう一度（60秒）', 'retry')}${play.continued ? '' : button('▶ +15秒で続ける（仮広告）', 'continue', 'secondary')}${canPractice ? button('時間無制限で練習', 'practice', 'ghost') : ''}${button('ホームへ', 'fail-home', 'ghost')}`);
  once('retry', () => terminalFail(() => play && startGame(play.stage, 'daily', play.date)));
  once('practice', () => terminalFail(() => play && startGame(play.stage, 'practice', play.date)));
  once('fail-home', () => terminalFail(showHome));
  once('continue', () => openAd('continue', () => {
    if (!play) return; play.continued = true; play.remaining = 15_000; closeDialog(); countdown(() => { if (!play) return; play.phase = 'running'; play.activeSince = performance.now(); play.deadline = performance.now() + play.remaining; play.timer = window.setInterval(tick, 100); });
  }));
}

function terminalFail(next: () => void): void {
  if (!play) return;
  save.failures[play.date] = (save.failures[play.date] ?? 0) + 1;
  log.event('stage_fail', { content_date: play.date, stage_id: play.stage.id, reason: 'timeout', found_count: play.found.size, wrong_tap_count: play.wrong, hint_count: Number(play.freeHint) + Number(play.rewardHint), attempt_no: play.attempt });
  persist(save); closeDialog(); next();
}

function completeGame(): void {
  if (!play || play.phase === 'done') return;
  if (play.timer) window.clearInterval(play.timer);
  const duration = Math.round(activeElapsed(play));
  play.elapsedMs = duration; play.activeSince = 0; play.phase = 'done';
  log.event('stage_complete', { content_date: play.date, stage_id: play.stage.id, mode: play.mode, clear_type: play.mode === 'practice' ? 'practice' : play.continued ? 'continued' : 'normal', duration_ms: duration, remaining_sec: Number.isFinite(play.remaining) ? Math.ceil(play.remaining / 1000) : -1, wrong_tap_count: play.wrong, free_hint_count: Number(play.freeHint), rewarded_hint_count: Number(play.rewardHint), continue_used: play.continued });
  let first = false;
  if (play.mode === 'daily' || play.mode === 'practice') {
    const result = recordClear(save, play.date, play.stage.id, play.mode === 'practice' ? 'practice' : play.continued ? 'continued' : 'normal'); first = result.first;
    log.event('streak_update', { content_date: play.date, streak_before: result.before, streak_after: result.after, result: !result.first ? 'same_day' : result.before > 0 && result.after === result.before + 1 ? 'increment' : 'reset' });
    if (first) log.event('daily_complete', { content_date: play.date, stage_id: play.stage.id, attempt_count: play.attempt, total_duration_ms: duration, streak_after: save.streakDays });
    persist(save);
  }
  showResult(first);
}

function showResult(first: boolean): void {
  if (!play) return;
  const label = play.mode === 'practice' ? '練習クリア' : play.continued ? '+15秒でクリア' : 'クリア！';
  shell(`<section class="card result"><div class="confetti">✦　●　★　✦</div><div class="medal">✓</div><h1>${label}</h1><p>${play.stage.name}の3つのちがいを見つけました。</p>${first ? `<div class="unlock">📔 今日のアルバムを開放<br><b>連続${save.streakDays}日</b></div>` : ''}${button('アルバムを見る', 'result-album')}${button('ホームへ', 'result-home', 'secondary')}</section>`);
  once('result-album', () => showAlbum('result')); once('result-home', showHome);
}

function abandonGame(reason: string): void {
  if (!play) return;
  if (play.timer) window.clearInterval(play.timer);
  if (play.phase === 'running' || play.phase === 'paused' || play.phase === 'failed') log.event('stage_abandon', { content_date: play.date, stage_id: play.stage.id, reason, elapsed_ms: Math.round(activeElapsed(play)), found_count: play.found.size });
  play = undefined; showHome();
}

function showAlbum(source: string): void {
  const date = today();
  log.event('album_view', { unlocked_count: Object.keys(save.clears).length, source });
  const cards = recentDates(date).map((key) => {
    const clear = save.clears[key]; const stage = clear ? stageById(clear.contentId) : stageForDate(key);
    return `<button class="album-card ${clear ? 'open' : ''}" data-date="${key}" ${clear ? '' : 'disabled'}><img src=".${stage?.base ?? stages[0].base}" alt="${stage?.name ?? '未開放'}"><b>${key.slice(5)}</b><span>${key === date ? '今日・' : ''}${clear ? (clear.clearType === 'practice' ? '練習 ✓' : 'クリア ✓') : '未クリア'}</span></button>`;
  }).join('');
  shell(`<header class="page-head"><h1>7日アルバム</h1><p>クリア済みだけ時間無制限で再挑戦できます</p></header><section class="album-grid">${cards}</section>`, nav());
  document.querySelectorAll<HTMLButtonElement>('.album-card.open').forEach((card) => card.addEventListener('click', () => { const key = card.dataset.date; const record = key ? save.clears[key] : undefined; const stage = record && stageById(record.contentId); if (key && stage) startGame(stage, 'replay', key); })); bindNav();
}

function showSettings(): void {
  shell(`<header class="page-head"><h1>設定</h1></header><section class="card settings"><label><span>効果音</span><input id="sound" type="checkbox" ${save.settings.sound ? 'checked' : ''}></label><label><span>振動</span><input id="vibration" type="checkbox" ${save.settings.vibration ? 'checked' : ''}></label><p>データはこの端末内だけに保存されます。外部送信はありません。</p></section>`, nav());
  (['sound', 'vibration'] as const).forEach((key) => document.querySelector(`#${key}`)?.addEventListener('change', (event) => { save.settings[key] = (event.target as HTMLInputElement).checked; persist(save); })); bindNav();
}

function showQa(): void {
  const options = stages.map((stage) => `<option value="${stage.id}">${stage.id} ${stage.name}（難度${stage.difficulty}）</option>`).join('');
  const latest = save.events.slice(-80).reverse().map((event) => `<li><b>${event.name}</b><small>${event.event_ts_utc}</small></li>`).join('');
  shell(`<header class="page-head"><h1>QAイベント確認</h1><p>ローカルログ ${save.events.length}件 / 外部送信なし</p></header><section class="card qa"><label>20問QAセレクタ<select id="qa-stage">${options}</select></label>${button('選択問題を時間無制限で確認', 'qa-play')}${button('JSONを保存', 'qa-export', 'secondary')}${button('進行不能をQA記録', 'qa-blocked', 'ghost')}<details open><summary>直近イベント</summary><ol class="event-list">${latest || '<li>イベントなし</li>'}</ol></details></section>`, nav());
  once('qa-play', () => { const stage = stageById((document.querySelector('#qa-stage') as HTMLSelectElement).value); if (stage) startGame(stage, 'replay', today()); });
  once('qa-export', () => { const blob = new Blob([JSON.stringify(save.events, null, 2)], { type: 'application/json' }); const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `spot-difference-events-${today()}.json`; link.click(); URL.revokeObjectURL(link.href); }); bindNav();
  once('qa-blocked', () => {
    const stageId = (document.querySelector('#qa-stage') as HTMLSelectElement).value;
    log.event('progression_blocked', { screen: 'qa', stage_id: stageId, error_code: 'manual_qa_report' });
    showQa();
  });
}

function showDialog(title: string, body: string, actions: string): void {
  let dialog = document.querySelector<HTMLDivElement>('#dialog');
  if (!dialog) { dialog = document.createElement('div'); dialog.id = 'dialog'; dialog.className = 'dialog-wrap'; document.body.append(dialog); }
  dialog.innerHTML = `<section class="dialog"><h2>${title}</h2>${body}<div class="dialog-actions">${actions}</div></section>`;
}
function closeDialog(): void { document.querySelector('#dialog')?.remove(); }

function openAd(placement: 'hint' | 'continue', reward: () => void): void {
  if (!play) return;
  const returnPhase = play.phase;
  let activationTimer: number | undefined;
  if (returnPhase === 'running') {
    play.remaining = Number.isFinite(play.remaining) ? Math.max(0, play.deadline - performance.now()) : play.remaining;
    if (play.timer) window.clearInterval(play.timer);
    pauseActive(play);
    play.phase = 'paused';
  }
  const cancel = (): void => {
    if (activationTimer !== undefined) window.clearTimeout(activationTimer);
    closeDialog();
    if (!play) return;
    if (returnPhase === 'failed') { play.phase = 'failed'; showFailureDialog(); }
    else if (returnPhase === 'running') resumePausedGame();
  };
  log.event('ad_mock_offer_view', { content_date: play.date, placement, stage_id: play.stage.id });
  showDialog('任意の広告プレビュー', '<p>実広告ではありません。最後まで確認した時だけ報酬を付与します。</p>', `${button('プレビューを見る', 'ad-accept')}${button('やめる', 'ad-decline', 'ghost')}`);
  once('ad-decline', () => { log.event('ad_mock_offer_response', { content_date: play?.date, placement, response: 'decline' }); cancel(); });
  once('ad-accept', () => {
    log.event('ad_mock_offer_response', { content_date: play?.date, placement, response: 'accept' }); log.event('ad_mock_show', { content_date: play?.date, placement, mock_duration_sec: 1 });
    showDialog('広告プレビュー', '<div class="ad-mock"><b>DEMO</b><span>実広告ではありません</span></div>', `${button('途中で閉じる', 'ad-close', 'ghost')}<button id="ad-complete" class="button primary" disabled>確認中…</button>`);
    once('ad-close', () => { log.event('ad_mock_dismiss', { content_date: play?.date, placement, completed: false, elapsed_ms: 0 }); cancel(); });
    activationTimer = window.setTimeout(() => {
      const done = document.querySelector<HTMLButtonElement>('#ad-complete');
      if (!done) return;
      done.disabled = false;
      done.textContent = '視聴完了';
      done.addEventListener('click', () => {
        if (activationTimer !== undefined) window.clearTimeout(activationTimer);
        log.event('ad_mock_complete', { content_date: play?.date, placement, reward_type: placement, reward_amount: placement === 'continue' ? 15 : 1 });
        log.event('ad_mock_dismiss', { content_date: play?.date, placement, completed: true, elapsed_ms: 1000 });
        closeDialog(); reward(); if (returnPhase === 'running') resumePausedGame();
      }, { once: true });
    }, 1000);
  });
}

function resumePausedGame(): void {
  if (!play || play.phase !== 'paused') return;
  countdown(() => {
    if (!play) return;
    play.phase = 'running';
    play.activeSince = performance.now();
    if (Number.isFinite(play.remaining)) { play.deadline = performance.now() + play.remaining; play.timer = window.setInterval(tick, 100); }
  });
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    backgroundStartedAt ??= Date.now();
  } else if (backgroundStartedAt !== undefined) {
    if (Date.now() - backgroundStartedAt >= 1_800_000) {
      log.startNewSession();
      save.sessionIndex += 1;
      log.event('session_start', { session_index: save.sessionIndex, days_since_install: Math.max(0, daysBetween(save.installDate, today())) });
    }
    backgroundStartedAt = undefined;
  }
  if (!play) return;
  if (document.hidden && play.phase === 'running') { if (Number.isFinite(play.remaining)) play.remaining = Math.max(0, play.deadline - performance.now()); if (play.timer) window.clearInterval(play.timer); pauseActive(play); play.phase = 'paused'; }
  else if (!document.hidden && play.phase === 'paused') resumePausedGame();
});

window.addEventListener('pagehide', () => {
  if (!play || !['running', 'paused', 'failed'].includes(play.phase)) return;
  if (play.timer) window.clearInterval(play.timer);
  log.event('stage_abandon', {
    content_date: play.date,
    stage_id: play.stage.id,
    reason: 'close',
    elapsed_ms: Math.max(0, Math.round(activeElapsed(play))),
    found_count: play.found.size,
  });
  play.phase = 'done';
});

window.addEventListener('error', (event) => log.event('app_error', { error_code: 'window_error', screen: play ? 'game' : 'app', message: String(event.message).slice(0, 120) }));

if (save.tutorialDone) showHome(); else showTutorial();
